import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const OrdersContext = createContext(null);
const STORAGE_KEY = 'sb_orders';

// Compute progress and status from creation timestamp.
// 0-15s: pending (0%) → 15s-3min: in progress (linear to 100%) → after 3min: delivered.
const computeOrderState = (createdAt) => {
  const elapsed = (Date.now() - new Date(createdAt).getTime()) / 1000; // seconds
  if (elapsed < 15) return { status: 'pending', label: 'En attente', progress: 0 };
  if (elapsed >= 180) return { status: 'delivered', label: 'Livrée', progress: 100 };
  const p = Math.min(99, Math.max(3, Math.round(((elapsed - 15) / (180 - 15)) * 100)));
  return { status: 'processing', label: 'En cours', progress: p };
};

export const OrdersProvider = ({ children }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
  }, [orders]);

  // Global ticker for live progress recomputation
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 3000);
    return () => clearInterval(t);
  }, []);

  const createOrder = useCallback((payload) => {
    const order = {
      id: `SB-${Date.now().toString(36).toUpperCase()}`,
      userId: user?.id || 'guest',
      email: user?.email || payload.email || null,
      items: payload.items,
      handle: payload.handle,
      total: payload.total,
      createdAt: new Date().toISOString()
    };
    setOrders(prev => [order, ...prev]);
    return order;
  }, [user]);

  const userOrders = orders.filter(o => o.userId === (user?.id || 'guest'));

  const withState = (order) => ({ ...order, state: computeOrderState(order.createdAt) });

  return (
    <OrdersContext.Provider value={{
      orders: orders.map(withState),
      userOrders: userOrders.map(withState),
      createOrder,
      _tick: tick
    }}>
      {children}
    </OrdersContext.Provider>
  );
};

export const useOrders = () => useContext(OrdersContext);
